/**
 * Users tab for the Company Profile page (CR-6247).
 *
 * A read view of who belongs to this company — the same list the supplier
 * profile shows. User administration itself stays in User Management, which is
 * linked from here rather than duplicated.
 */
import { Loader2, Users, Search, Shield, AlertCircle, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import type { UserProfile } from '../../lib/api';

interface UsersTabProps {
  users: UserProfile[];
  filteredUsers: UserProfile[];
  loading: boolean;
  error: string | null;
  search: string;
  onSearchChange: (search: string) => void;
  companyName: string;
  /**
   * CR-6250: hide the Manage Users link. It goes to the viewer's OWN User
   * Management, which reads as an offer to administer this company's users
   * when a supplier is looking at one of its customers.
   */
  hideManageUsers?: boolean;
}

/** First initials, falling back to the email when no name is set. */
function initials(userProfile: UserProfile): string {
  const first = userProfile.user.first_name?.[0];
  const last = userProfile.user.last_name?.[0] || '';
  if (first) return `${first}${last}`;
  return userProfile.user.email[0].toUpperCase();
}

/**
 * List this company's users with a client-side search.
 *
 * @param users - all users returned for the company
 * @param filteredUsers - `users` narrowed by `search`
 */
export function UsersTab({
  users,
  filteredUsers,
  loading,
  error,
  search,
  onSearchChange,
  companyName,
  hideManageUsers = false,
}: UsersTabProps) {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        {!hideManageUsers && (
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/users')}>
            <ExternalLink className="h-4 w-4 mr-2" />
            Manage Users
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="flex items-start justify-center gap-2 py-12 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>
            {search
              ? 'No users match your search'
              : `No users found for ${companyName}`}
          </p>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {filteredUsers.length} of {users.length} {users.length === 1 ? 'user' : 'users'}
          </p>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {filteredUsers.map((userProfile) => (
              <Card key={userProfile.id}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-lg font-semibold text-primary">
                        {initials(userProfile)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium flex items-center gap-2">
                        <span className="truncate">
                          {userProfile.user.full_name || userProfile.user.email.split('@')[0]}
                        </span>
                        {userProfile.role_details?.is_admin_role && (
                          <Shield className="h-4 w-4 text-amber-500 shrink-0" aria-label="Admin" />
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground truncate">
                        {userProfile.job_title || 'No title'}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 text-sm text-muted-foreground truncate">
                    {userProfile.user.email}
                  </div>

                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {userProfile.role_details?.name && (
                      <Badge variant="outline" className="text-xs">
                        {userProfile.role_details.name}
                      </Badge>
                    )}
                    {!userProfile.user.is_active && (
                      <Badge variant="secondary" className="text-xs">Inactive</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default UsersTab;
